import type {
  AwardCriteriaDto,
  DesignContestJuryMemberDto,
  DesignContestPrizeDto,
  FoaPart,
  SourcingEventDto,
} from "../../../../../autogen/bff-api";
import { AwardCriterionType, Regulation } from "../../../../../autogen/bff-api";
import { isFoaNationalTier, isOrdinaryEeaTier } from "./karens";
import { isDesignContest } from "./procedure";

export { isDesignContest };

// FOA § 5-1 (5): Del V covers a design contest from this value, below the EEA threshold too.
export const DESIGN_CONTEST_PART_V_NOK = 1_630_000;

export const eventIsDesignContest = (event: SourcingEventDto): boolean =>
  event.eventContext.type === "Public" && isDesignContest(event.eventContext.procedureType);

export const prizeLabel = (prize: DesignContestPrizeDto): string =>
  prize.value?.value != null
    ? `${prize.description ?? ""} (${prize.value.value.toLocaleString("nb-NO")} ${prize.value.currency})`
    : (prize.description ?? "");

export const designContestPrizesOf = (event: SourcingEventDto): string[] =>
  event.eventContext.type === "Public" ? (event.eventContext.designContest?.prizes ?? []).map(prizeLabel) : [];

// Mirrors SourcingEvent.tenderAwardCriteria in the BFF: a contest is ranked on the jury's criteria alone.
export const tenderAwardCriteriaOf = (event: SourcingEventDto): AwardCriteriaDto | null | undefined =>
  eventIsDesignContest(event) ? null : event.eventData.awardCriteria;

// Mirrors FieldKeys.DesignContestWithinPartV in the BFF.
export const offersDesignContest = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  estimatedValueNok: number | null | undefined,
): boolean =>
  isOrdinaryEeaTier(regulation, foaPart) ||
  (regulation === Regulation.Foa &&
    isFoaNationalTier(regulation, foaPart) &&
    estimatedValueNok != null &&
    estimatedValueNok >= DESIGN_CONTEST_PART_V_NOK);

// Mirrors FieldKeys.DesignContestClimateConsiderationStated: FOA Part II contests go out on the national E3 notice.
export const announcesDesignContestNationally = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
): boolean => regulation === Regulation.Foa && isFoaNationalTier(regulation, foaPart);

// Mirrors DesignContestClimateConsideration.NATIONAL_TYPES in the BFF.
export const DESIGN_CONTEST_CLIMATE_TYPES: readonly AwardCriterionType[] = [
  AwardCriterionType.QualityEnvironmentalSpecification,
  AwardCriterionType.QualityEnvironmentalExemptInsignificant,
  AwardCriterionType.QualityEnvironmentalExemptCriticalInterest,
  AwardCriterionType.QualityEnvironmentalExemptNationalMinimum,
];

// Mirrors DesignContest.holdsQualifiedThird in the BFF.
export const holdsQualifiedThird = (jury: readonly DesignContestJuryMemberDto[]): boolean =>
  jury.length > 0 && jury.filter((member) => member.holdsRequiredQualification).length * 3 >= jury.length;
